/**
 * `/500` — the runtime-failure surface, and the body of the router's root
 * error boundary.
 *
 * Two callers, one composition. The route entry is a reader who typed or was
 * sent to the address, with no failing request behind them; the boundary is a
 * render that actually threw, and it passes the mapped reason and the
 * correlation id it saw. Anything the second caller knows and the first does
 * not is rendered only when it is present, never invented.
 *
 * **The correlation id is printed only when a response carried one.** It is
 * never generated here — nginx is its only source — and a stale id from an
 * unrelated successful call is worse than no id at all, because a reader
 * quoting it sends support to the wrong request.
 *
 * **No status code and no error code reach the surface.** The code goes to the
 * log; the reader gets the fix.
 *
 * The retry is a `<button>` rather than the mockup's link-upgraded-to-reload.
 * That link exists so the page's only primary action is not inert with
 * scripting off — a guarantee a React bundle cannot make in the first place,
 * since with scripting off there is no page — and an `<a>` with no destination
 * promises a document to a screen-reader user and offers a context menu of
 * navigation actions that all fail.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { IconHome, IconRetry, IconServerFault } from '@shared/chrome/icons';

import {
  ERROR_ACTION_ICON,
  ERROR_ACTION_PRIMARY,
  ERROR_ACTION_SECONDARY,
  ErrorSurface,
} from '../_shared/ErrorSurface';
import { serverErrorCopy } from './copy';

export interface ServerErrorPageProps {
  /**
   * The mapped sentence from the shared error catalog, where a real failure
   * produced one. It states WHAT HAPPENED and sits ahead of the standing copy,
   * which states what was not affected — the two halves the design system's
   * error shape asks for. Absent on a direct visit, where nothing happened
   * that this page could describe.
   */
  reason?: string;
}

export function ServerErrorPage({ reason }: ServerErrorPageProps) {
  useEffect(() => {
    document.title = serverErrorCopy.documentTitle;
  }, []);

  return (
    <ErrorSurface
      kind="server"
      glyph={<IconServerFault />}
      heading={serverErrorCopy.heading}
      body={
        <>
          {reason ? <span className="block">{reason}</span> : null}
          <span className={reason ? 'mt-2 block' : undefined}>{serverErrorCopy.body}</span>
        </>
      }
      actions={
        <>
          <button type="button" onClick={() => window.location.reload()} className={ERROR_ACTION_PRIMARY}>
            <IconRetry className={ERROR_ACTION_ICON} />
            {serverErrorCopy.actions.retry}
          </button>
          <Link to="/" className={ERROR_ACTION_SECONDARY}>
            <IconHome className={ERROR_ACTION_ICON} />
            {serverErrorCopy.actions.home}
          </Link>
        </>
      }
      footnote={
        <p className="m-0 text-xs text-ink-muted">
          {serverErrorCopy.report.before}
          <Link to="/cases/new" className="text-ink underline">
            {serverErrorCopy.report.link}
          </Link>
          {serverErrorCopy.report.after}
        </p>
      }
    />
  );
}
